import { format, UTCDate } from '@/utils/dates';
import { createColumnHelper } from '@tanstack/react-table';
import logoMonzo from '../../../assets/images/logo-monzo.svg';
import logoRevolut from '../../../assets/images/logo-revolut.svg';
import logoXero from '../../../assets/images/logo-xero.svg';
import { AvatarNameDescription } from '../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Button } from '../../../design-system/button/button';

/**
 * EXPERIMENTAL: Get the appropriate logo for a XERO-adapter account based on account name.
 * Falls back to Xero logo if no specific match is found.
 * Used for accounts imported from Xero that don't have institution data.
 */
function experimentalGetXeroAccountLogo(accountName: string): string {
  const nameLower = accountName.toLowerCase();
  if (nameLower.includes('revolut')) {
    return logoRevolut;
  }
  if (nameLower.includes('monzo')) {
    return logoMonzo;
  }
  return logoXero;
}

export interface BankFeedConnection {
  id: string;
  seapointAccountId: string;
  seapointAccountName: string;
  externalAccountId: string;
  externalAccountName: string;
  currency: string;
  lastSyncDate?: string;
  isConnected: boolean;
  /** Whether this is a Xero-only account (exists in Xero but not in Seapoint) */
  isXeroOnly?: boolean;
  fspAdapter?: string;
  institutionLogo?: string;
  institutionName?: string;
  availableBalance?: number | null;
  /** Balance from external system (BankSummary for Xero accounts) */
  externalAccountBalance?: number;
}

const columnHelper = createColumnHelper<BankFeedConnection>();

const DATE_FORMAT = 'dd MMM yyyy';

export const bankFeedsTableColumnDefinition = [
  columnHelper.accessor('seapointAccountName', {
    header: 'Account name',
    cell: ({ row }) => {
      const institutionName = row.original.institutionName;
      const currency = row.original.currency;
      const institutionLogo = row.original.institutionLogo;
      const balance = row.original.availableBalance;
      const fspAdapter = row.original.fspAdapter;
      const accountName = row.original.seapointAccountName;

      // Match accounts page: show institution name (if available) + currency
      const displayName = institutionName ? `${institutionName} (${currency})` : `${accountName} (${currency})`;

      // EXPERIMENTAL: For XERO-adapter accounts without institution logo, use logo based on account name
      const avatarUrl =
        institutionLogo || (fspAdapter === 'XERO' ? experimentalGetXeroAccountLogo(accountName) : undefined);

      // Format balance
      const balanceText =
        balance != null
          ? `${currency} ${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : undefined;
      return (
        <AvatarNameDescription
          avatar={{ showAvatar: true, avatarUrl, size: 'sm' }}
          name={displayName}
          description={balanceText}
          className="w-56"
        />
      );
    },
    size: 30,
  }),
  columnHelper.accessor('externalAccountName', {
    header: 'Xero account',
    cell: ({ getValue, row }) => {
      const name = getValue();
      const isConnected = row.original.isConnected;

      if (!isConnected) {
        return <p className="text-sm text-muted-foreground italic">Not connected</p>;
      }

      return <p className="text-sm text-black">{name}</p>;
    },
    size: 35,
  }),
  columnHelper.accessor('lastSyncDate', {
    header: 'Last sync',
    cell: ({ getValue, row }) => {
      const lastSyncDate = getValue();
      const isConnected = row.original.isConnected;
      const fspAdapter = row.original.fspAdapter;

      // Only show sync date for accounts that sync data (Modulr syncs TO Xero, XERO imports FROM Xero)
      const showsSyncDate = fspAdapter === 'MODULR' || fspAdapter === 'XERO';
      if (!showsSyncDate) {
        return null;
      }

      if (!isConnected) {
        return null;
      }

      if (!lastSyncDate) {
        return <p className="text-sm text-muted-foreground">Not synced</p>;
      }

      const formatted = format(new UTCDate(lastSyncDate), DATE_FORMAT);
      return <p className="text-sm text-black">{formatted}</p>;
    },
    size: 20,
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row, table }) => {
      const onDisconnect = table.options.meta?.onDisconnect;
      const onReconnect = table.options.meta?.onReconnect;
      const isDisconnecting = table.options.meta?.isDisconnecting;
      const isConnected = row.original.isConnected;

      return (
        <div className="flex justify-end">
          <Button
            variant="link"
            size="default"
            onClick={() => {
              if (isConnected) {
                onDisconnect?.(row.original.id);
              } else {
                onReconnect?.(row.original.id);
              }
            }}
            disabled={isDisconnecting}
            className={isConnected ? 'text-destructive' : 'text-link'}
          >
            {isConnected ? 'Disconnect' : 'Connect'}
          </Button>
        </div>
      );
    },
    size: 15,
    minSize: 15,
  }),
];
